import React, { useCallback, useEffect, useState } from "react";
import MUIDataTable from "mui-datatables";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, Switch, Tab, Tabs } from "@mui/material";
import { Close, DeleteOutline, Edit } from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Axios from "../axios";
import { useAuth } from "../auth/authContext";
import { findNavItemByPath } from "../navigation/navDataUtils";

const ROWS_PER_PAGE = 10;

const CoachManagement = () => {
  const { navData } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [write, setWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("all");
  const [sortOrder, setSortOrder] = useState({ name: "", direction: "asc" });
  const [deleteState, setDeleteState] = useState({ status: false, id: "" });
  const [deactivateConfirm, setDeactivateConfirm] = useState({ status: false, id: "", message: "" });
  const BACKEND_URL = process.env.REACT_APP_BACKEND;

  const showError = (err) => {
    toast.error(err?.response?.data?.message || err?.response?.data?.error || "Something went wrong");
  };

  useEffect(() => {
    const matchingNavItem = findNavItemByPath(navData, location.pathname);
    setWrite(Boolean(matchingNavItem?.write));
  }, [location.pathname, navData]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const fetchCoaches = useCallback(() => {
    setLoading(true);
    Axios.get(`${BACKEND_URL}coach`, {
      params: {
        page: page + 1,
        limit: ROWS_PER_PAGE,
        search: search || undefined,
        type: selectedType !== "all" ? selectedType : undefined,
        sortField: sortOrder.name || undefined,
        sortOrder: sortOrder.direction || undefined,
      },
    })
      .then((res) => {
        setRows(res?.data?.data?.docs || []);
        setTotalCount(res?.data?.data?.totalDocs || 0);
      })
      .catch(showError)
      .finally(() => setLoading(false));
  }, [BACKEND_URL, page, search, selectedType, sortOrder]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCoaches();
  }, [fetchCoaches]);

  const deleteCoach = () => {
    setLoading(true);
    Axios.delete(`${BACKEND_URL}coach/${deleteState.id}`)
      .then((res) => {
        toast.success(res?.data?.message || "Deleted successfully");
        setDeleteState({ status: false, id: "" });
        fetchCoaches();
      })
      .catch(showError)
      .finally(() => setLoading(false));
  };

  const toggleStatus = (id, currentActive, force = false) => {
    setLoading(true);
    const request = currentActive
      ? Axios.patch(`${BACKEND_URL}coach/${id}/deactivate`, {}, force ? { params: { force: true } } : undefined)
      : Axios.patch(`${BACKEND_URL}coach/${id}/activate`);

    request
      .then((res) => {
        toast.success(res?.data?.message || "Status updated");
        setDeactivateConfirm({ status: false, id: "", message: "" });
        fetchCoaches();
      })
      .catch((err) => {
        if (currentActive && !force && err?.response?.data?.requiresConfirmation) {
          setDeactivateConfirm({ status: true, id, message: err.response.data.message });
          return;
        }
        showError(err);
      })
      .finally(() => setLoading(false));
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "host":
        return "secondary";
      case "club":
        return "success";
      case "coach":
      default:
        return "primary";
    }
  };

  const columns = [
    {
      name: "profileimg",
      label: "Profile Image",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => {
          const img = rows[i]?.profileimg || rows[i]?.coachImage;
          return img ? (
            <img
              src={img}
              alt=""
              style={{ width: 50, height: 50, objectFit: "cover", borderRadius: "50%", backgroundColor: "#f7f7f7", border: "1px solid #ddd" }}
            />
          ) : (
            <Box sx={{ width: 50, height: 50, borderRadius: "50%", bgcolor: "#eee", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", color: "#666" }}>
              N/A
            </Box>
          );
        },
      },
    },
    {
      name: "coachName",
      label: "Name",
      options: {
        customBodyRenderLite: (i) => rows[i]?.coachName || "-",
      },
    },
    {
      name: "title",
      label: "Title",
      options: {
        customBodyRenderLite: (i) => rows[i]?.title || "-",
      },
    },
    {
      name: "type",
      label: "Type",
      options: {
        customBodyRenderLite: (i) => {
          const typeVal = rows[i]?.type || "coach";
          return (
            <Chip
              label={typeVal.toUpperCase()}
              size="small"
              color={getTypeColor(typeVal)}
              variant="filled"
              sx={{ fontWeight: 600, fontSize: "0.9rem" }}
            />
          );
        },
      },
    },
    {
      name: "clubSubType",
      label: "Club Sub Type",
      options: {
        customBodyRenderLite: (i) => {
          const row = rows[i];
          if (row?.type !== "club") return "-";
          return row?.clubSubType === "cycling_club" ? "Cycling Club" : row?.clubSubType === "running_club" ? "Running Club" : "-";
        },
      },
    },
    {
      name: "location",
      label: "Location",
      options: {
        customBodyRenderLite: (i) => rows[i]?.location || "-",
      },
    },
    {
      name: "status",
      label: "Status",
      options: {
        customBodyRenderLite: (i) => (rows[i]?.is_active ? "Active" : "Inactive"),
      },
    },
    write && {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (i) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={Boolean(rows[i]?.is_active)}
              disabled={loading}
              onChange={() => toggleStatus(rows[i]?._id, rows[i]?.is_active)}
              size="small"
            />
            <Edit sx={{ color: "#1976d2", fontSize: 20, cursor: "pointer" }} onClick={() => navigate(`/coach-management/edit/${rows[i]?._id}`)} />
            <DeleteOutline
              sx={{ color: "red", fontSize: 20, cursor: "pointer" }}
              onClick={() => setDeleteState({ status: true, id: rows[i]?._id })}
            />
          </Box>
        ),
      },
    },
  ].filter(Boolean);

  return (
    <div className="container-fluid">
      <Helmet>
        <title>Coach / Host / Club Management</title>
      </Helmet>
      <div className="d-flex justify-content-between align-center mb-3">
        <h2>Coach, Host & Club Management</h2>
        {write && (
          <Button variant="contained" onClick={() => navigate("/coach-management/add")}>
            Create Coach / Host / Club
          </Button>
        )}
      </div>

      <Tabs
        value={selectedType}
        onChange={(_, val) => {
          setPage(0);
          setSelectedType(val);
        }}
        className="mb-4"
        sx={{
          "& button": {
            color: "#000",
            fontWeight: 700,
            fontSize: "1.4rem",
            textTransform: "uppercase",
            transition: "all 0.3s",
          },
          "& button:hover": {
            color: "#3dd2cc",
          },
          "& button.Mui-selected": {
            color: "#3dd2cc",
            fontWeight: "bold",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "black",
            height: "3px",
          },
        }}
      >
        <Tab className="fs-14 fw-600 p-2 mr-2" label="ALL" value="all" />
        <Tab className="fs-14 fw-600 p-2 mr-2" label="COACHES" value="coach" />
        <Tab className="fs-14 fw-600 p-2 mr-2" label="CLUBS" value="club" />
        <Tab className="fs-14 fw-600 p-2 mr-2" label="HOSTS" value="host" />
      </Tabs>

      <MUIDataTable
        title="Coaches, Hosts & Clubs List"
        data={rows}
        columns={columns}
        options={{
          responsive: "standard",
          selectableRows: "none",
          filter: false,
          viewColumns: false,
          download: false,
          serverSide: true,
          search: true,
          searchText: searchInput,
          count: totalCount,
          page,
          sortOrder,
          rowsPerPage: ROWS_PER_PAGE,
          rowsPerPageOptions: [],
          textLabels: { body: { noMatch: loading ? "Loading..." : "No records found" } },
          onTableChange: (action, tableState) => {
            if (action === "changePage") {
              setPage(tableState.page);
            } else if (action === "sort") {
              setSortOrder(tableState.sortOrder);
              setPage(0);
            }
          },
          onSearchChange: (searchQuery) => {
            setSearchInput(searchQuery || "");
          },
          onSearchClose: () => {
            setSearchInput("");
          },
        }}
      />

      <Dialog open={deleteState.status} onClose={() => setDeleteState({ status: false, id: "" })} fullWidth>
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Delete Record</h3>
          <IconButton onClick={() => setDeleteState({ status: false, id: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>Are you sure you want to delete this record?</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeleteState({ status: false, id: "" })}>
            Cancel
          </Button>
          <Button className="submit-button" variant="contained" disabled={loading} onClick={deleteCoach}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deactivateConfirm.status}
        onClose={() => setDeactivateConfirm({ status: false, id: "", message: "" })}
        fullWidth
      >
        <div className="d-flex justify-content-between align-center p-3">
          <h3>Deactivate Chat Room</h3>
          <IconButton onClick={() => setDeactivateConfirm({ status: false, id: "", message: "" })}>
            <Close />
          </IconButton>
        </div>
        <DialogContent dividers>{deactivateConfirm.message}</DialogContent>
        <DialogActions>
          <Button className="cancel-button" onClick={() => setDeactivateConfirm({ status: false, id: "", message: "" })}>
            Cancel
          </Button>
          <Button
            className="submit-button"
            variant="contained"
            color="error"
            disabled={loading}
            onClick={() => toggleStatus(deactivateConfirm.id, true, true)}
          >
            Yes, Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CoachManagement;